import { useMediaQuery } from "@base-ui/react/unstable-use-media-query";
import {
  type ComponentPropsWithoutRef,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  useBlockingLayerHost,
  usePortalHost,
} from "../../providers/portal-host";
import { BlockingActionStatus } from "../blocking-action-overlay/blocking-action-overlay";
import { Footer } from "../footer/footer";
import { FullPageLoadingIndicator } from "../full-page-loading/full-page-loading";
import { IconButton } from "../icon-button";
import { Stack } from "../layout";
import {
  type AppShellBlockingAction,
  AppShellContext,
  useAppShellContext,
} from "./app-shell-context";
import { PageHeader } from "./page-header";
import { SidebarToggle } from "./sidebar-toggle";

const appShellStyles = [
  "grid",
  "h-dvh",
  "min-h-0",
  "overflow-hidden",
  "bg-surface",
  "grid-rows-[auto_minmax(0,1fr)]",
  "transition-[grid-template-columns]",
  "duration-200",
  "ease-in-out",
  "[--app-shell-sidebar-expanded-width:260px]",
  "[--app-shell-sidebar-collapsed-width:72px]",
  "[--app-shell-sidebar-width:var(--app-shell-sidebar-expanded-width)]",
  "data-[sidebar-collapsed]:[--app-shell-sidebar-width:var(--app-shell-sidebar-collapsed-width)]",
  "grid-cols-[var(--app-shell-sidebar-width)_minmax(0,1fr)]",
  "[&>[data-slot=top-bar]]:col-span-2",
  // Keep the header brand region at the expanded width when the sidebar collapses.
  "[&_[data-slot=top-bar-leading]]:w-(--app-shell-sidebar-expanded-width)",
  "max-[599px]:grid-cols-1",
  "max-[599px]:[&>[data-slot=top-bar]]:col-span-1",
  "max-[599px]:[&_[data-slot=top-bar-leading]]:w-auto",
].join(" ");

const sidebarToggleHotkey = "[";
const sidebarCollapsedStorageKey = "assetwatch:app-shell:sidebar-collapsed";

type ActiveBlockingAction = AppShellBlockingAction & {
  id: number;
};

function readSidebarCollapsedPreference() {
  if (typeof window === "undefined") return false;

  try {
    return window.localStorage.getItem(sidebarCollapsedStorageKey) === "true";
  } catch {
    return false;
  }
}

function persistSidebarCollapsedPreference(collapsed: boolean) {
  try {
    window.localStorage.setItem(sidebarCollapsedStorageKey, String(collapsed));
  } catch {
    // Browsers can disable storage access; retain the in-memory preference.
  }
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;

  return (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT" ||
    target.isContentEditable
  );
}

export interface AppShellProps
  extends Omit<ComponentPropsWithoutRef<"div">, "children" | "className"> {
  children: ReactNode;
}

/**
 * Coordinates directly composed application chrome, desktop sidebar state, and
 * narrow-viewport navigation. Child Sidebar and AppShell controls use its
 * state automatically.
 */
function AppShell({ children, ...props }: AppShellProps) {
  const isMobile = useMediaQuery("(max-width: 599px)", {
    defaultMatches: false,
  });
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(
    readSidebarCollapsedPreference,
  );
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const [blockingActions, setBlockingActions] = useState<
    ActiveBlockingAction[]
  >([]);
  const nextBlockingActionId = useRef(0);
  const activeBlockingAction = blockingActions.at(-1);
  const portalHost = usePortalHost();
  const blockingLayerHost = useBlockingLayerHost();

  function setDesktopSidebarCollapsedPreference(collapsed: boolean) {
    setDesktopSidebarCollapsed(collapsed);
    persistSidebarCollapsedPreference(collapsed);
  }

  function beginBlockingAction(action: AppShellBlockingAction) {
    const id = nextBlockingActionId.current;
    nextBlockingActionId.current += 1;
    setBlockingActions((current) => [...current, { ...action, id }]);

    return () => {
      setBlockingActions((current) =>
        current.filter((blockingAction) => blockingAction.id !== id),
      );
    };
  }

  useEffect(() => {
    if (isMobile) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (
        event.defaultPrevented ||
        event.isComposing ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.shiftKey ||
        isEditableTarget(event.target) ||
        event.key !== sidebarToggleHotkey
      ) {
        return;
      }

      event.preventDefault();
      const collapsed = !desktopSidebarCollapsed;
      setDesktopSidebarCollapsed(collapsed);
      persistSidebarCollapsedPreference(collapsed);
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [desktopSidebarCollapsed, isMobile]);

  useEffect(() => {
    if (!portalHost) return;

    portalHost.inert = Boolean(activeBlockingAction);
    return () => {
      portalHost.inert = false;
    };
  }, [activeBlockingAction, portalHost]);

  const blockingOverlay = activeBlockingAction ? (
    <div
      className="fixed inset-0 z-[var(--z-layer-blocking)] flex items-center justify-center bg-surface-scrim"
      data-slot="app-shell-blocking-overlay"
    >
      <BlockingActionStatus label={activeBlockingAction.label} />
    </div>
  ) : null;

  return (
    <AppShellContext
      value={{
        desktopSidebarCollapsed,
        beginBlockingAction,
        isMobile,
        mobileNavigationOpen,
        setDesktopSidebarCollapsed: setDesktopSidebarCollapsedPreference,
        setMobileNavigationOpen,
      }}
    >
      <div className="relative h-dvh">
        <div
          {...props}
          aria-busy={activeBlockingAction ? true : undefined}
          className={appShellStyles}
          data-sidebar-collapsed={desktopSidebarCollapsed ? "" : undefined}
          data-slot="app-shell"
          inert={activeBlockingAction ? true : undefined}
        >
          {children}
        </div>
        {blockingOverlay &&
          (blockingLayerHost
            ? createPortal(blockingOverlay, blockingLayerHost)
            : blockingOverlay)}
      </div>
    </AppShellContext>
  );
}

interface AppShellContentBaseProps
  extends Omit<ComponentPropsWithoutRef<"div">, "children" | "className"> {
  /** Optional full-width notification region below the top bar. Supports one or more banners. */
  banner?: ReactNode;
  /** Route-provided content displayed in the shared page-header chrome. */
  pageHeader?: ReactNode;
  /** Optional full-width route tabs below the page-header content. */
  pageHeaderTabs?: ReactNode;
  /** Optional localized label for the Privacy Policy link in the page footer. */
  privacyPolicyLabel?: string;
  /** Optional destination for the Privacy Policy link in the page footer. */
  privacyPolicyHref?: string;
  children: ReactNode;
}

type AppShellContentProps =
  | (AppShellContentBaseProps & {
      /** Replaces page content with the full-page loading animation. */
      pending?: false;
    })
  | (AppShellContentBaseProps & {
      /** Replaces page content with the full-page loading animation. */
      pending: true;
    });

/** Page frame with optional banner and header regions above its padded, scrollable body. */
function AppShellContent({
  banner,
  pageHeader,
  pageHeaderTabs,
  privacyPolicyLabel,
  privacyPolicyHref,
  children,
  pending = false,
  ...props
}: AppShellContentProps) {
  if (pending) {
    return (
      <div
        {...props}
        className="grid min-h-0 min-w-0 grid-rows-[minmax(0,1fr)] overflow-hidden bg-surface-low"
        data-slot="app-shell-content"
      >
        <main
          aria-busy="true"
          className="flex min-h-0 min-w-0 items-center justify-center overflow-y-auto bg-surface-low p-(--spacing-24)"
        >
          <FullPageLoadingIndicator />
        </main>
      </div>
    );
  }

  return (
    <div
      {...props}
      className="relative z-0 grid min-h-0 min-w-0 grid-cols-[minmax(0,1fr)] grid-rows-[auto_auto_minmax(0,1fr)] overflow-hidden bg-surface-low"
      data-slot="app-shell-content"
    >
      {banner && (
        <Stack data-slot="app-shell-banner" fullWidth gap={0}>
          {banner}
        </Stack>
      )}
      {pageHeader && (
        <PageHeader tabs={pageHeaderTabs} data-slot="app-shell-page-header">
          {pageHeader}
        </PageHeader>
      )}
      <main
        className="row-start-3 flex min-h-0 min-w-0 flex-col overflow-y-auto bg-surface-low px-(--spacing-24) pt-(--spacing-24)"
        data-slot="app-shell-content-body"
      >
        <div className="min-w-0 grow">{children}</div>
        <div className="mt-(--spacing-32) shrink-0">
          <Footer
            privacyPolicyHref={privacyPolicyHref}
            privacyPolicyLabel={privacyPolicyLabel}
          />
        </div>
      </main>
    </div>
  );
}

export interface AppShellMobileNavigationTriggerProps
  extends Omit<
    ComponentPropsWithoutRef<typeof IconButton>,
    "aria-label" | "icon" | "onClick" | "variant"
  > {}

/** Mobile-only control that opens the AppShell navigation drawer. */
function AppShellMobileNavigationTrigger({
  ...props
}: AppShellMobileNavigationTriggerProps) {
  const appShell = useAppShellContext();

  if (!appShell) {
    throw new Error(
      "AppShellMobileNavigationTrigger must be rendered within AppShell.",
    );
  }

  if (!appShell.isMobile) {
    return null;
  }

  return (
    <IconButton
      {...props}
      aria-label="Open navigation"
      icon="menu-hamburger"
      onClick={() => appShell.setMobileNavigationOpen(true)}
      variant="ghost"
    />
  );
}

interface AppShellSidebarToggleProps
  extends Omit<
    ComponentPropsWithoutRef<"button">,
    "aria-expanded" | "aria-label" | "children" | "className"
  > {}

/** Desktop-only control for the shell-owned sidebar collapse state. */
export function AppShellSidebarToggle({
  onClick,
  ...props
}: AppShellSidebarToggleProps) {
  const appShell = useAppShellContext();

  if (!appShell) {
    throw new Error("AppShellSidebarToggle must be rendered within AppShell.");
  }

  if (appShell.isMobile) {
    return null;
  }

  const { desktopSidebarCollapsed, setDesktopSidebarCollapsed } = appShell;

  return (
    <SidebarToggle
      {...props}
      collapsed={desktopSidebarCollapsed}
      onClick={onClick}
      onToggle={() => setDesktopSidebarCollapsed(!desktopSidebarCollapsed)}
      shortcut={sidebarToggleHotkey}
    />
  );
}

export {
  AppShell,
  AppShellContent,
  AppShellMobileNavigationTrigger,
  type AppShellContentProps,
};
